import React, { useState, useRef, useEffect } from 'react';
import './style.scss';

export interface UQToggleMenuItem {
  text: string;
  icon?: string;
  url?: string;
  disabled?: boolean;
}

export interface UQToggleMenuProps {
  items: UQToggleMenuItem[];
  triggerElement: React.ReactNode; // The element that toggles the menu
  position?: 'left' | 'right' | 'top' | 'bottom'; // Optional fixed position
  onItemClick?: (item: UQToggleMenuItem) => void;
  className?: string;
}

const UQToggleMenu: React.FC<UQToggleMenuProps> = ({
  items,
  triggerElement,
  position,
  onItemClick,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'left' | 'right' | 'top' | 'bottom' | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const handleItemClick = (item: UQToggleMenuItem) => {
    if (item.disabled) return;
    onItemClick?.(item);
    setIsOpen(false); // Close the menu after clicking an item
  };

  const calculatePosition = () => {
    if (position) {
      setMenuPosition(position);
      return;
    }

    if (triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (triggerRect.bottom + menuRect.height <= viewportHeight) {
        setMenuPosition('bottom');
      } else if (triggerRect.top - menuRect.height >= 0) {
        setMenuPosition('top');
      } else if (triggerRect.left + menuRect.width <= viewportWidth) {
        setMenuPosition('right');
      } else {
        setMenuPosition('left');
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen]);

  return (
    <div className={`uq-togglemenu-container ${className}`} ref={triggerRef}>
      <div onClick={toggleMenu} className="uq-togglemenu-trigger">
        {triggerElement}
      </div>
      {isOpen && (
        <div
          ref={menuRef}
          className={`uq-togglemenu-menu uq-togglemenu-${menuPosition}`}
        >
          <ul>
            {items.map((item) => (
              <li
                key={item.text}
                className={`uq-togglemenu-item ${item.disabled ? 'disabled' : ''}`}
                onClick={() => handleItemClick(item)}
              >
                {item.icon && <span className={`uq-togglemenu-icon ${item.icon}`} />}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UQToggleMenu;
